/**
 * Pricing System Types & Interfaces
 * 
 * Centralized type definitions for the pricing calculation system.
 * This file contains all interfaces, types, and constants used across
 * the pricing calculator components.
 * 
 * @author Wildlife Project Team
 * @version 2.1.0
 */

export interface PriceBreakdownItem {
  id: string;
  label: string;
  amount: number;
  description: string;
  type: 'base' | 'option' | 'choice' | 'multiplier';
}

export interface CalculationResult {
  basePrice: number;
  scaledBasePrice: number; // Actual calculated base price after scaling
  totalPrice: number;
  breakdown: PriceBreakdownItem[];
  currency: string;
  currencySymbol: string;
}

export interface FormValues {
  [optionLabel: string]: any;
}

export type BasePricingType = 'fixed' | 'per_person' | 'per_day' | 'per_person_day';

export type PricingType = 
  | 'flat'           // Legacy: Fixed amount
  | 'fixed'          // New: Fixed amount (equivalent to flat)
  | 'percentage'     // Legacy: Percentage of base price
  | 'per_unit'       // Legacy: Price with multiplierType
  | 'per_person'     // New: Price multiplied by people
  | 'per_day'        // New: Price multiplied by days
  | 'per_person_day' // New: Price multiplied by people × days
  ;

export interface PricingRule {
  type: PricingType;
  value: number;
  description?: string;
  multiplierType?: 'people' | 'days' | 'both'; // Legacy per_unit support
}

export interface PriceCalculatorConfig {
  basePrice: number;
  currency: string;
  basePriceType: BasePricingType;
}

/**
 * Keywords used for auto-detection of people and days input fields
 */
export const PEOPLE_KEYWORDS = ['people', 'person', 'participant', 'guest', 'traveler'] as const;
export const DAYS_KEYWORDS = ['day', 'days', 'duration', 'night', 'nights', 'length'] as const;

/**
 * Default values for pricing calculations
 */
export const DEFAULT_VALUES = {
  PEOPLE: 1,
  DAYS: 1,
  BASE_PRICE_TYPE: 'fixed' as BasePricingType,
} as const; 